import { FieldTypes } from '../../../constants/FieldTypes'
import {
  BooleanField,
  DropdownField,
  Field,
  FieldMap,
  InfoStage,
} from '../../../types/forms/fields'
import { createFileField } from '../../../utils/fields'
import { selectCoverageOption } from './utils'

export const confirmPartnerPlanField: BooleanField = {
  type: FieldTypes.BOOLEAN,
  condition: v =>
    selectCoverageOption('multiple-options')(v) &&
    v?.multipleOptionsDescription === 'partner-individual',
  placeholder: "I can enroll in my partner/family member's plan",
  // yesText: 'Continue',
  // noText: 'Go Back',
}

export const multipleCoverageOptionsDescriptionField: DropdownField = {
  type: FieldTypes.DROPDOWN,
  condition: selectCoverageOption('multiple-options'),
  labels: [{ text: 'Please choose one' }],
  placeholder: 'Multiple options description',
  options: [
    {
      text: 'My partner / family member has a plan I believe I can enroll in, and/or I can get a plan as an individual through New York State of Health.',
      id: 'partner-individual',
    },
    {
      text: 'My partner / family member has a plan that I believe I can enroll in, and I also have a coverage option through my employer',
      id: 'partner-employer',
    },
    {
      text: 'My employer is offering multiple plan options',
      id: 'multiple-employer',
    },
    {
      text: 'Other',
      id: 'other',
    },
  ],
}

const partnerIndividualNote: InfoStage = {
  info: true,
  name: 'Notice',
  condition: v =>
    selectCoverageOption('multiple-options')(v) &&
    v?.multipleOptionsDescription === 'partner-individual',
  data: [
    {
      type: 'span',
      children: [
        {
          text: `You can only enroll in your parents' plan if you are under 26 years old. \n
You can only enroll in your partner's plan if you are married or in a registered domestic partnership in New York state`,
        },
      ],
    },
  ],
}
export const confirmPartnerIndividualNote: InfoStage = {
  info: true,
  name: 'Notice',
  condition: v =>
    selectCoverageOption('multiple-options')(v) &&
    v?.multipleOptionsDescription === 'partner-individual' &&
    v?.confirmPartnerPlan === true,
  data: [
    {
      type: 'span',
      children: [
        {
          text: `Please use information on your partner / family member's plan to complete this form so we can provide an assessment.\n 
You may need to contact HR in order to get all the necessary information.`,
        },
      ],
    },
  ],
}

const partnerEmployerNote: InfoStage = {
  info: true,
  name: 'Notice',
  condition: v =>
    selectCoverageOption('multiple-options')(v) &&
    v?.multipleOptionsDescription === 'partner-employer',
  data: [
    {
      type: 'span',
      children: [
        {
          text: `Hamilton Billing provides 1 free assessment per patient. 
Any additional plans you would like us to assess are $25 per assessment.\n
We recommend you choose your current plan for us to assess, or the plan you will likely be on two months prior to your estimated due date`,
        },
      ],
    },
  ],
}

const multipleEmployerNote: InfoStage = {
  info: true,
  name: 'Notice',
  condition: v =>
    selectCoverageOption('multiple-options')(v) &&
    v?.multipleOptionsDescription === 'multiple-employer',
  data: [
    {
      type: 'span',
      children: [
        {
          text: `Please ask your HR department for the following: \n
1) A PDF overview of your plan options
2) Please ask your HR the following questions on all the plans being offered:`,
        },
      ],
    },
  ],
}

export const assessmentOnCurrentPlanNote: InfoStage = {
  info: true,
  name: 'Notice',
  condition: v =>
    selectCoverageOption('multiple-options')(v) &&
    v?.multipleOptionsDescription === 'partner-employer' &&
    v?.planToAssess === 'current-plan',
  data: [
    {
      type: 'span',
      children: [
        {
          text: 'Please complete the "Primary Coverage" section with your current plan and complete the "Secondary Coverage" section with your alternative insurance plan',
        },
      ],
    },
  ],
}

export const assessmentOnPartnerPlanNote: InfoStage = {
  info: true,
  name: 'Notice',
  condition: v =>
    selectCoverageOption('multiple-options')(v) &&
    v?.multipleOptionsDescription === 'partner-employer' &&
    v?.planToAssess === 'partner-plan',
  data: [
    {
      type: 'span',
      children: [
        {
          text: 'Please complete the "Primary Coverage" section with your partner/family member\'s plan and complete the "Secondary Coverage" section with your current plan',
        },
      ],
    },
  ],
}

export const multipleCoverageOptionsDetailsFields: Record<string, Field | InfoStage> = {
  partnerIndividualNote,
  partnerEmployerNote,
  multipleEmployerNote,
  confirmPartnerPlan: confirmPartnerPlanField,
  // confirmPartnerIndividualNote,
  planToAssess: {
    condition: v =>
      selectCoverageOption('multiple-options')(v) &&
      v?.multipleOptionsDescription === 'partner-employer',
    type: FieldTypes.DROPDOWN,
    placeholder: 'Which plan do you want an assessment on?',
    options: [
      {
        text: 'I want an assessment on my current plan',
        id: 'current-plan',
      },
      {
        text: 'I want an assessment on a plan through my partner / family member',
        id: 'partner-plan',
      },
    ],
  },
  // assessmentOnCurrentPlanNote,
  // assessmentPartnerPlanNote,
  planCoverageType: {
    condition: v =>
      selectCoverageOption('multiple-options')(v) &&
      v?.multipleOptionsDescription === 'multiple-employer',
    type: FieldTypes.TEXTAREA,
    labels: [
      {
        text: 'Are all the plans fully insured, self funded, or split-funded?',
      },
    ],
    placeholder: 'Plans fully insured/self-funded/split-funded',
  },
  reimbursementSystem: {
    condition: v =>
      selectCoverageOption('multiple-options')(v) &&
      v?.multipleOptionsDescription === 'multiple-employer',
    type: FieldTypes.TEXTAREA,
    labels: [
      {
        text: 'How do these plans determine reimbursement for OON providers? (Please clarify if they are using the reasonable and customary system versus the Medicare fee schedule)',
      },
    ],
    placeholder: 'Reimbursement System',
  },
  reimbursementRate: {
    condition: v =>
      selectCoverageOption('multiple-options')(v) &&
      v?.multipleOptionsDescription === 'multiple-employer',
    type: FieldTypes.TEXTAREA,
    labels: [
      {
        text: 'Please indicate the percentage of that system (Ex: 80% reasonable and customary; 110% medicare rates)',
      },
    ],
    placeholder: 'Reimbursement rate',
  },
  employmentOptionsFile: createFileField({
    condition: v =>
      selectCoverageOption('multiple-options')(v) &&
      v?.multipleOptionsDescription === 'multiple-employer',
    placeholder: 'Employment options PDF',
    fileType: 'pdf',
    name: 'employment-options',
  }),
  coverageStartDate: {
    condition: v =>
      selectCoverageOption('multiple-options')(v) &&
      v?.multipleOptionsDescription === 'multiple-employer',
    type: FieldTypes.DATE,
    placeholder: 'Coverage start date',
    labels: [{ text: 'When will coverage start?' }],
  },
  multipleOptionsOtherNotes: {
    condition: v =>
      selectCoverageOption('multiple-options')(v) && v?.multipleOptionsDescription === 'other',
    type: FieldTypes.TEXTAREA,
    placeholder: 'Multiple options explanation',
    labels: [
      {
        text: 'Please explain...',
      },
    ],
  },
}

export const multipleCoverageOptionsFields: Record<string, Field | InfoStage> = {
  multipleOptionsDescription: multipleCoverageOptionsDescriptionField,
  ...multipleCoverageOptionsDetailsFields,
}

export const multipleCoverageOptionsDetailsField: FieldMap = {
  name: 'Multiple coverage options details',
  children: multipleCoverageOptionsDetailsFields,
}

export const multipleCoverageOptionsField: FieldMap = {
  name: 'Please specify',
  children: multipleCoverageOptionsFields,
}
